import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBetaEnd } from '../context/BetaEndContext';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, CreditCard, CalendarCheck, Loader2, ArrowRight, Clock, ChevronLeft, X, Zap, Rocket, Building2, Flame, PartyPopper } from 'lucide-react';
import { toast } from 'sonner';

/**
 * BetaEndedModal — shown when the beta program has ended and the workspace
 * hasn't chosen a path yet.
 *
 *   - Always-visible until the user either subscribes OR clicks "Back to
 *     workspace" (workspace then runs in a paused/read-only state).
 *   - 24h discount window (managed server-side; this component just renders
 *     the remaining time and the discounted prices).
 *   - Amber-glowing border + bigger layout per design feedback.
 */
export default function BetaEndedModal() {
  const { api } = useAuth();
  const { status, showMandatoryModal, dismissed, setDismissed, discountExpiresIn } = useBetaEnd();
  const location = useLocation();
  // Restore the last view the user was on before being redirected to Stripe —
  // so clicking "back" from Stripe Checkout brings them exactly where they left.
  const [view, setView] = useState(() => {
    try {
      const saved = sessionStorage.getItem('betaEndedModal:view');
      if (saved === 'plans' || saved === 'schedule' || saved === 'choose' || saved === 'booked') return saved;
    } catch { /* ignore */ }
    return 'choose';
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [planLoading, setPlanLoading] = useState(null);
  const [bookedAt, setBookedAt] = useState(null); // human-readable confirmed time
  const iframeLoadedRef = useRef(false);

  const skipOnPath = /^\/(login|signup|set-password|beta-onboarding|billing-setup|signup)/i.test(location.pathname || '');

  // Persist the current view so a Stripe roundtrip returns to the same sub-view
  useEffect(() => {
    try { sessionStorage.setItem('betaEndedModal:view', view); } catch { /* ignore */ }
  }, [view]);

  useEffect(() => {
    if (view !== 'schedule' || iframeLoadedRef.current) return;
    iframeLoadedRef.current = true;
    const s = document.createElement('script');
    s.src = 'https://links.skyeai.io/js/form_embed.js';
    s.async = true;
    document.body.appendChild(s);
  }, [view]);

  // Listen for the LeadConnector/skyeai booking-complete postMessage so we can celebrate,
  // show the confirmed time, and give the user a clean "back to workspace" CTA.
  useEffect(() => {
    if (view !== 'schedule') return;
    const handler = (ev) => {
      const data = ev?.data;
      if (!data) return;
      // LeadConnector fires various event names — match any that signal a successful booking.
      const raw = typeof data === 'string' ? data : JSON.stringify(data);
      const looksBooked = /appointment.?booked|onAppointmentBooked|EVENT_CALENDAR_APPOINTMENT_BOOKED|booking.?complete|slot.?booked/i.test(raw);
      if (!looksBooked) return;

      // Try to extract a start time from common field shapes
      let when = null;
      try {
        const obj = typeof data === 'object' ? data : JSON.parse(raw);
        const candidate =
          obj?.data?.startTime ||
          obj?.startTime ||
          obj?.appointment?.startTime ||
          obj?.payload?.startTime ||
          obj?.slot?.startTime ||
          obj?.start ||
          null;
        if (candidate) {
          const dt = new Date(candidate);
          if (!isNaN(dt.getTime())) {
            when = dt.toLocaleString(undefined, {
              weekday: 'short', month: 'short', day: 'numeric',
              hour: 'numeric', minute: '2-digit',
            });
          }
        }
      } catch { /* ignore parse errors */ }

      setBookedAt(when);
      setView('booked');

      // Confetti celebration — z-index above the modal overlay (modal is z-120)
      (async () => {
        try {
          const mod = await import('canvas-confetti');
          const c = mod.default;
          c({ particleCount: 160, spread: 95, origin: { y: 0.3 }, zIndex: 9999, colors: ['#fbbf24', '#f59e0b', '#2dd4bf', '#a78bfa', '#f472b6'] });
          setTimeout(() => c({ particleCount: 110, spread: 80, origin: { y: 0.45 }, zIndex: 9999, colors: ['#fbbf24', '#2dd4bf', '#14b8a6'] }), 300);
          setTimeout(() => c({ particleCount: 70, spread: 100, origin: { x: 0.2, y: 0.5 }, zIndex: 9999, colors: ['#fbbf24', '#2dd4bf'] }), 600);
          setTimeout(() => c({ particleCount: 70, spread: 100, origin: { x: 0.8, y: 0.5 }, zIndex: 9999, colors: ['#fbbf24', '#2dd4bf'] }), 600);
        } catch { /* optional */ }
      })();
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [view]);

  const buyLifetime = async () => {
    setCheckoutLoading(true);
    try {
      // Remember we launched from the choose view — so if user cancels, they return here
      try { sessionStorage.setItem('betaEndedModal:view', 'choose'); } catch { /* ignore */ }
      const res = await api.post('/api/billing/lifetime-checkout');
      const url = res.data?.checkout_url;
      if (url) window.location.href = url;
      else toast.error('Checkout could not be created. Contact support.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unable to start checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const startPlanCheckout = async (plan) => {
    setPlanLoading(plan);
    try {
      // Ensure the plans view is persisted — so Stripe cancel returns here
      try { sessionStorage.setItem('betaEndedModal:view', 'plans'); } catch { /* ignore */ }
      const res = await api.post('/api/billing/plan-checkout', { plan });
      const url = res.data?.checkout_url;
      if (url) window.location.href = url;
      else toast.error('Checkout could not be created. Contact support.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Unable to start checkout');
    } finally {
      setPlanLoading(null);
    }
  };

  // Visible only when the backend says the flow is active AND user hasn't dismissed yet
  const visible = showMandatoryModal && !skipOnPath;
  const reopenable = status?.show_end_modal && dismissed && !skipOnPath;

  if (!visible && !reopenable) return null;

  const discountActive = !!status?.discount_active;
  const lifetimeLabel = status?.lifetime_price_label || '$5,000';
  const prices = status?.plan_prices || {};

  const fmtTime = (secs) => {
    if (!secs || secs <= 0) return null;
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${String(h).padStart(2, '0')}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
  };
  const timeLeft = discountActive ? fmtTime(discountExpiresIn) : null;

  const planCards = [
    { slug: 'launchpad', name: 'Launchpad', icon: Rocket,    color: 'teal',    desc: '1 sub-account' },
    { slug: 'starter',   name: 'Starter',   icon: Zap,       color: 'violet',  desc: '3 sub-accounts' },
    { slug: 'agency',    name: 'Agency',    icon: Building2, color: 'emerald', desc: '10 sub-accounts' },
    { slug: 'pro',       name: 'Pro',       icon: Flame,     color: 'amber',   desc: 'Unlimited sub-accounts' },
  ];

  // Banner re-open mode (when dismissed)
  if (!visible && reopenable) return null; // the banner in the navbar handles this

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className={`fixed inset-0 z-[120] flex justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto ${view === 'schedule' ? 'items-start' : 'items-center'}`}
        data-testid="beta-ended-modal"
      >
        <motion.div
          initial={{ scale: 0.92, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          transition={{ type: 'spring', damping: 22 }}
          className={`relative w-full rounded-2xl my-4 ${view === 'schedule' ? 'max-w-3xl p-5 sm:p-6' : view === 'booked' ? 'max-w-2xl p-9 sm:p-12' : 'max-w-4xl p-8 sm:p-10 my-8'}`}
          style={{
            background: 'linear-gradient(180deg,#0f172a,#0a0b0f)',
            border: '2px solid rgba(251, 191, 36, 0.55)',
            boxShadow: '0 0 0 1px rgba(251,191,36,0.15), 0 25px 70px -20px rgba(251,191,36,0.35), 0 0 80px -10px rgba(251,191,36,0.25)',
          }}
        >
          {/* Subtle amber corner glow */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full blur-3xl opacity-40" style={{ background: 'radial-gradient(circle, #fbbf24 0%, transparent 70%)' }} />

          {/* Standard header is hidden on the booked confirmation view — that
              view uses its own celebratory layout below. */}
          {view !== 'booked' && (
          <div className={`text-center relative ${view === 'schedule' ? 'mb-4' : 'mb-7'}`}>
            <div className={`inline-flex items-center gap-1.5 rounded-full text-[11px] font-extrabold tracking-[0.25em] ${view === 'schedule' ? 'px-3 py-1 mb-2' : 'px-3.5 py-1.5 mb-4'}`}
                 style={{ background: 'linear-gradient(135deg,#fbbf24,#f59e0b)', color: '#0a0b0f' }}>
              BETA PROGRAM HAS ENDED
            </div>
            <h2 className={`font-semibold text-white leading-tight ${view === 'schedule' ? 'text-2xl sm:text-3xl' : 'text-3xl sm:text-4xl'}`}>
              {view === 'choose' ? 'Thank you for testing the Beta' :
               view === 'plans' ? 'Choose your monthly plan' :
               'Schedule a quick call'}
            </h2>
            {view !== 'schedule' && (
              <p className="text-slate-300 text-base mt-3 max-w-2xl mx-auto">
                {view === 'choose'
                  ? 'Pick how you\'d like to continue below — subscribe, go lifetime, or schedule a chat. Your workspace is paused until you choose an option.'
                  : 'Pick the tier that fits. Cancel anytime from Billing.'}
              </p>
            )}

            {discountActive && view !== 'schedule' && (
              <div className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold tracking-wider"
                   style={{ background: 'rgba(251,191,36,0.12)', border: '1px solid rgba(251,191,36,0.4)', color: '#fcd34d' }}>
                <Clock size={14} className="animate-pulse" />
                <span>LAST CHANCE DEAL · {timeLeft || 'expires soon'}</span>
              </div>
            )}
          </div>
          )}

          {/* Three main options */}
          {view === 'choose' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setView('plans')}
                className="group rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-teal-400/40 p-6 text-left transition-all"
                data-testid="beta-ended-monthly-btn"
              >
                <CreditCard className="text-teal-300 mb-4" size={30} />
                <h3 className="text-white font-semibold text-lg mb-1.5">Monthly plan</h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {discountActive
                    ? <>Free is available. Your discount applies to eligible paid plans.</>
                    : <>Start free or choose the paid tier that fits.</>}
                </p>
                <div className="mt-5 inline-flex items-center gap-1 text-teal-300 text-sm font-medium group-hover:gap-2 transition-all">
                  Choose plan <ArrowRight size={15} />
                </div>
              </button>

              <button
                onClick={buyLifetime}
                disabled={checkoutLoading}
                className="group rounded-xl border-2 border-amber-400/50 bg-gradient-to-b from-amber-400/15 to-amber-400/[0.03] hover:from-amber-400/20 p-6 text-left transition-all relative overflow-hidden disabled:opacity-60"
                data-testid="beta-ended-lifetime-btn"
              >
                <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider text-amber-200 bg-amber-400/20 border border-amber-400/40">
                  BEST VALUE
                </div>
                <Crown className="text-amber-300 mb-4" size={30} />
                <h3 className="text-white font-semibold text-lg mb-1.5">LIFETIME</h3>
                <p className="text-sm text-amber-200/90 leading-relaxed">
                  {discountActive
                    ? <>One payment of <s className="text-amber-200/50">$5,000</s> <b className="text-amber-200">{lifetimeLabel}</b> — never pay again.</>
                    : <>One payment of <span className="font-bold">{lifetimeLabel}</span> — never pay again.</>}
                </p>
                <div className="mt-5 inline-flex items-center gap-1 text-amber-200 text-sm font-medium group-hover:gap-2 transition-all">
                  {checkoutLoading ? <Loader2 size={15} className="animate-spin" /> : 'Secure my lifetime'} <ArrowRight size={15} />
                </div>
              </button>

              <button
                onClick={() => setView('schedule')}
                className="group rounded-xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-purple-400/40 p-6 text-left transition-all"
                data-testid="beta-ended-schedule-btn"
              >
                <CalendarCheck className="text-purple-300 mb-4" size={30} />
                <h3 className="text-white font-semibold text-lg mb-1.5">Schedule a call</h3>
                <p className="text-sm text-slate-400 leading-relaxed">Not sure yet? Let's talk through options.</p>
                <div className="mt-5 inline-flex items-center gap-1 text-purple-300 text-sm font-medium group-hover:gap-2 transition-all">
                  Book a time <ArrowRight size={15} />
                </div>
              </button>
            </div>
          )}

          {view === 'plans' && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {planCards.map(({ slug, name, icon: Icon, color, desc }) => {
                  const info = prices[slug] || {};
                  const amtC = info.amount_cents ?? 0;
                  const regC = info.regular_cents ?? 0;
                  const amt = (amtC / 100).toFixed(0);
                  const reg = (regC / 100).toFixed(0);
                  const isDisc = !!info.is_discounted && amtC !== regC;
                  return (
                    <button
                      key={slug}
                      onClick={() => startPlanCheckout(slug)}
                      disabled={planLoading === slug}
                      className={`group rounded-xl border p-5 text-left transition-all relative overflow-hidden disabled:opacity-60 ${
                        color === 'teal' ? 'border-white/10 hover:border-teal-400/40 bg-white/[0.03] hover:bg-teal-400/[0.06]' :
                        color === 'violet' ? 'border-white/10 hover:border-violet-400/40 bg-white/[0.03] hover:bg-violet-400/[0.06]' :
                        color === 'emerald' ? 'border-white/10 hover:border-emerald-400/40 bg-white/[0.03] hover:bg-emerald-400/[0.06]' :
                        'border-white/10 hover:border-amber-400/40 bg-white/[0.03] hover:bg-amber-400/[0.06]'
                      }`}
                      data-testid={`beta-ended-plan-${slug}`}
                    >
                      {isDisc && (
                        <div className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wider text-amber-200 bg-amber-400/20 border border-amber-400/40">
                          SAVE ${reg - amt}
                        </div>
                      )}
                      <Icon className={`${color === 'teal' ? 'text-teal-300' : color === 'violet' ? 'text-violet-300' : color === 'emerald' ? 'text-emerald-300' : 'text-amber-300'} mb-3`} size={24} />
                      <h4 className="text-white font-semibold text-lg">{name}</h4>
                      <div className="mt-1 flex items-baseline gap-2">
                        <span className="text-2xl font-bold text-white tabular-nums">${amt}</span>
                        {isDisc && <s className="text-slate-500 text-sm">${reg}</s>}
                        <span className="text-xs text-slate-400">/mo</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-2 leading-relaxed">{desc}</p>
                      <div className={`mt-3 inline-flex items-center gap-1 text-sm font-medium group-hover:gap-2 transition-all ${
                        color === 'teal' ? 'text-teal-300' : color === 'violet' ? 'text-violet-300' : color === 'emerald' ? 'text-emerald-300' : 'text-amber-300'
                      }`}>
                        {planLoading === slug ? <Loader2 size={14} className="animate-spin" /> : <>Start plan <ArrowRight size={14} /></>}
                      </div>
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setView('choose')}
                className="mt-5 inline-flex items-center gap-1 text-slate-400 hover:text-white text-sm font-medium"
                data-testid="beta-ended-plans-back-btn"
              >
                <ChevronLeft size={16} /> Back
              </button>
            </div>
          )}

          {view === 'schedule' && (
            <div>
              <div className="rounded-xl overflow-hidden border border-white/10 bg-white">
                <iframe
                  src="https://links.skyeai.io/widget/booking/UTSmNKx10xCIHjDQ6Zfe"
                  style={{ width: '100%', border: 'none', height: 'min(70vh, 560px)' }}
                  scrolling="yes"
                  id="UTSmNKx10xCIHjDQ6Zfe_endmodal"
                  title="Schedule a call"
                  data-testid="beta-ended-skyeai-iframe"
                />
              </div>
              <button
                onClick={() => setView('choose')}
                className="mt-3 w-full px-4 py-2 rounded-xl bg-white/5 text-slate-300 hover:bg-white/10 text-sm font-medium"
                data-testid="beta-ended-back-btn"
              >
                ← Back to options
              </button>
            </div>
          )}

          {/* Booked confirmation — appears after the calendar widget postMessages a success event */}
          {view === 'booked' && (
            <div className="text-center relative" data-testid="beta-ended-booked-view">
              <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-6"
                   style={{
                     background: 'linear-gradient(135deg,#14b8a6,#2dd4bf)',
                     boxShadow: '0 0 60px -5px rgba(45,212,191,0.7)',
                   }}>
                <PartyPopper className="text-[#0a0b0f]" size={36} />
              </div>
              <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-[11px] font-extrabold tracking-[0.25em] mb-4"
                   style={{ background: 'linear-gradient(135deg,#2dd4bf,#14b8a6)', color: '#0a0b0f' }}>
                BOOKING CONFIRMED
              </div>
              <h2 className="text-4xl sm:text-5xl font-semibold text-white leading-tight">
                Thanks, you&apos;re booked!
              </h2>
              <p className="text-slate-300 text-base sm:text-lg mt-4 max-w-lg mx-auto">
                We&apos;ll see you soon — check your email for the calendar invite.
              </p>

              {bookedAt && (
                <div className="mt-6 inline-flex items-center gap-2.5 px-5 py-3 rounded-xl border border-teal-400/40 bg-teal-400/10">
                  <CalendarCheck className="text-teal-300" size={18} />
                  <span className="text-base font-semibold text-teal-100">{bookedAt}</span>
                </div>
              )}

              <div className="mt-8 flex flex-col sm:flex-row items-stretch justify-center gap-3">
                <button
                  onClick={() => { setBookedAt(null); setView('choose'); setDismissed(true); }}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base hover:scale-[1.02] transition-transform"
                  style={{ background: 'linear-gradient(135deg,#2dd4bf,#14b8a6)', color: '#0a0b0f', boxShadow: '0 10px 28px -6px rgba(45,212,191,0.55)' }}
                  data-testid="beta-ended-booked-back-to-workspace-btn"
                >
                  <ArrowRight size={18} /> Take me back to workspace
                </button>
                <button
                  onClick={() => { setBookedAt(null); setView('choose'); }}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-base font-semibold text-white bg-white/[0.06] hover:bg-white/[0.12] border-2 border-white/15 hover:border-white/30 transition-colors"
                  data-testid="beta-ended-booked-view-plans-btn"
                >
                  View plans anyway
                </button>
              </div>
            </div>
          )}

          {/* Back to workspace — available only from the main chooser */}
          {view === 'choose' && (
            <div className="mt-7 pt-5 border-t border-white/5 flex items-center justify-center">
              <button
                onClick={() => setDismissed(true)}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-sm text-slate-300 hover:text-white hover:bg-white/[0.05] transition-colors"
                data-testid="beta-ended-dismiss-btn"
              >
                <X size={15} /> Back to workspace (paused)
              </button>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
